package one.devsbox.www.ui.users.edit

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import compose.assets.Svg
import compose.assets.svg.ContentSaveOutline
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.tools.PermissionWrapperEditor
import one.devsbox.www.ui.widgets.ContentActions
import one.devsbox.www.ui.widgets.FullscreenErrorUI
import one.devsbox.www.ui.widgets.FullscreenLoadingUI
import one.devsbox.www.ui.widgets.LabelField
import one.devsbox.www.ui.widgets.LabelSection
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState

@Composable
@ComposableNavNode(ArgsId::class)
fun UserEUI() {
    with(UserECtx.get()) {
        ContentUserEUI()
    }
}

@Composable
context(ctx: UserECtx)
private fun ContentUserEUI() {
    ctx.vm.warningMessage?.let { msg ->
        AlertDialog(
            onDismissRequest = { ctx.action.clearWarning() },
            title = { Text(stringResource(Res.string.ui_users_edit_error_title)) },
            text = { Text(msg) },
            confirmButton = {
                TextButton(onClick = { ctx.action.clearWarning() }) {
                    Text(stringResource(Res.string.ui_users_edit_btn_ok))
                }
            }
        )
    }

    Box(modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> Box(modifier = Modifier.fillMaxSize()) {
                    SuccessUI(state.nnvl)
                    ContentActions(
                        contentActions = ctx.vm.contentActions,
                        colorInactive = ctx.colorSecondaryContainer,
                        colorActive = ctx.colorPrimaryContainer
                    )
                }
            }
        }
    }
}

@Composable
context(ctx: UserECtx)
private fun SuccessUI(editor: UserECtx.Editor) {
    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val isWide = maxWidth >= 1000.dp
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(16.dp)
                .verticalScroll(rememberScrollState())
        ) {
            if (isWide) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Column(
                        modifier = Modifier.weight(1f),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        UserProfileSection(editor)
                        RolesSection(editor)
                    }
                    Box(modifier = Modifier.weight(1.5f)) {
                        PermissionsMatrixSection(editor)
                    }
                }
            } else {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    UserProfileSection(editor)
                    RolesSection(editor)
                    PermissionsMatrixSection(editor)
                }
            }

            Spacer(modifier = Modifier.height(250.dp))
        }
    }
}

@Composable
context(ctx: UserECtx)
private fun UserProfileSection(editor: UserECtx.Editor) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            LabelSection(text = stringResource(Res.string.ui_users_edit_title_user_profile))

            Spacer(modifier = Modifier.height(16.dp))

            FieldGroup(stringResource(Res.string.ui_users_edit_label_full_name)) {
                OutlinedTextField(
                    value = editor.name.text,
                    onValueChange = { editor.name.set(it) },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(4.dp),
                    singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedContainerColor = Color.Transparent,
                        unfocusedContainerColor = Color.Transparent,
                        focusedBorderColor = MaterialTheme.colorScheme.primary,
                        unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant
                    )
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            FieldGroup(stringResource(Res.string.ui_users_edit_label_login_id)) {
                OutlinedTextField(
                    value = editor.login.text,
                    onValueChange = { editor.login.set(it) },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(4.dp),
                    singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedContainerColor = Color.Transparent,
                        unfocusedContainerColor = Color.Transparent,
                        focusedBorderColor = MaterialTheme.colorScheme.primary,
                        unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant
                    )
                )
            }
        }
    }
}

@Composable
context(ctx: UserECtx)
private fun PermissionsMatrixSection(editor: UserECtx.Editor) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            LabelSection(text = stringResource(Res.string.ui_users_detail_title_permissions_matrix))

            Spacer(modifier = Modifier.height(16.dp))
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)

            // Table Header
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(vertical = 12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(modifier = Modifier.weight(1.5f)) {
                    LabelField(text = stringResource(Res.string.ui_users_detail_header_category))
                }
                Box(modifier = Modifier.weight(1f), contentAlignment = Alignment.Center) {
                    LabelField(text = stringResource(Res.string.ui_users_detail_header_view))
                }
                Box(modifier = Modifier.weight(1f), contentAlignment = Alignment.Center) {
                    LabelField(text = stringResource(Res.string.ui_users_detail_header_edit))
                }
                Box(modifier = Modifier.weight(1f), contentAlignment = Alignment.Center) {
                    LabelField(text = stringResource(Res.string.ui_users_detail_header_delete))
                }
            }
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)

            PermissionRow(
                label = stringResource(resource = Res.string.ui_users_detail_cat_media),
                item = editor.permissions.media,
                itemMe = ctx.myPermissions.media
            )
            PermissionRow(
                label = stringResource(Res.string.ui_users_detail_cat_campaigns),
                item = editor.permissions.campaign,
                itemMe = ctx.myPermissions.campaign
            )
            PermissionRow(
                label = stringResource(Res.string.ui_users_detail_cat_devices),
                item = editor.permissions.device,
                itemMe = ctx.myPermissions.device
            )
            PermissionRow(
                label = stringResource(Res.string.ui_users_detail_cat_locations),
                item = editor.permissions.location,
                itemMe = ctx.myPermissions.location
            )
            PermissionRow(
                label = stringResource(Res.string.ui_users_detail_cat_users),
                item = editor.permissions.user,
                itemMe = ctx.myPermissions.user
            )
            PermissionRow(
                label = stringResource(Res.string.ui_users_detail_cat_company),
                item = editor.permissions.company,
                itemMe = ctx.myPermissions.company,
                showDivider = false
            )
        }
    }
}

@Composable
private fun FieldGroup(label: String, content: @Composable () -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(0.dp)) {
        LabelField(text = label)
        content()
    }
}

context(ctx: UserECtx) @Composable
private fun PermissionRow(
    label: String,
    item: PermissionWrapperEditor.Item,
    showDivider: Boolean = true,
    itemMe: SharedIO.Permission.PermissionWrapper.Item
) {
    Column {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = 16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = label,
                modifier = Modifier.weight(1.5f),
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )
            Checkbox(
                checked = item.read.state,
                enabled = !ctx.editingMyAccount && itemMe.read,
                onCheckedChange = { item.read.set(it) },
                modifier = Modifier.weight(1f)
            )
            Checkbox(
                checked = item.write.state,
                enabled = !ctx.editingMyAccount && itemMe.write,
                onCheckedChange = { item.write.set(it) },
                modifier = Modifier.weight(1f)
            )
            Checkbox(
                checked = item.delete.state,
                enabled = !ctx.editingMyAccount && itemMe.delete,
                onCheckedChange = { item.delete.set(it) },
                modifier = Modifier.weight(1f)
            )
        }
        if (showDivider) HorizontalDivider(
            color = MaterialTheme.colorScheme.outlineVariant.copy(
                alpha = 0.5f
            )
        )
    }
}

enum class UserRole {
    Admin, Technic, CampaignPublisher, CampaignViewer
}

context(ctx: UserECtx)
private fun applyRole(role: UserRole, editor: UserECtx.Editor, onWarning: (List<String>) -> Unit) {
    if (ctx.editingMyAccount) {
        onWarning(listOf("Cannot modify your own account permissions"))
        return
    }

    val targetPerms = when (role) {
        UserRole.Admin -> mapOf(
            "media" to Triple(true, true, true),
            "campaign" to Triple(true, true, true),
            "device" to Triple(true, true, false),
            "location" to Triple(true, true, false),
            "user" to Triple(true, true, true),
            "company" to Triple(false, false, false),
        )

        UserRole.Technic -> mapOf(
            "media" to Triple(true, true, false),
            "campaign" to Triple(false, false, false),
            "device" to Triple(true, true, false),
            "location" to Triple(true, true, false),
            "user" to Triple(false, false, false),
            "company" to Triple(false, false, false),
        )

        UserRole.CampaignPublisher -> mapOf(
            "media" to Triple(true, true, true),
            "campaign" to Triple(true, true, true),
            "device" to Triple(true, false, false),
            "location" to Triple(true, false, false),
            "user" to Triple(false, false, false),
            "company" to Triple(false, false, false),
        )

        UserRole.CampaignViewer -> mapOf(
            "media" to Triple(true, false, false),
            "campaign" to Triple(true, false, false),
            "device" to Triple(true, false, false),
            "location" to Triple(true, false, false),
            "user" to Triple(false, false, false),
            "company" to Triple(false, false, false),
        )
    }

    val missingPermissions = mutableListOf<String>()

    fun checkAndAdd(
        categoryName: String,
        actionName: String,
        hasMe: Boolean,
        targetVal: Boolean,
        currentVal: Boolean
    ) {
        if (targetVal != currentVal && !hasMe) {
            missingPermissions.add("$categoryName: $actionName")
        }
    }

    checkAndAdd(
        "Media",
        "View",
        ctx.myPermissions.media.read,
        targetPerms["media"]!!.first,
        editor.permissions.media.read.state
    )
    checkAndAdd(
        "Media",
        "Edit",
        ctx.myPermissions.media.write,
        targetPerms["media"]!!.second,
        editor.permissions.media.write.state
    )
    checkAndAdd(
        "Media",
        "Delete",
        ctx.myPermissions.media.delete,
        targetPerms["media"]!!.third,
        editor.permissions.media.delete.state
    )

    checkAndAdd(
        "Campaign",
        "View",
        ctx.myPermissions.campaign.read,
        targetPerms["campaign"]!!.first,
        editor.permissions.campaign.read.state
    )
    checkAndAdd(
        "Campaign",
        "Edit",
        ctx.myPermissions.campaign.write,
        targetPerms["campaign"]!!.second,
        editor.permissions.campaign.write.state
    )
    checkAndAdd(
        "Campaign",
        "Delete",
        ctx.myPermissions.campaign.delete,
        targetPerms["campaign"]!!.third,
        editor.permissions.campaign.delete.state
    )

    checkAndAdd(
        "Device",
        "View",
        ctx.myPermissions.device.read,
        targetPerms["device"]!!.first,
        editor.permissions.device.read.state
    )
    checkAndAdd(
        "Device",
        "Edit",
        ctx.myPermissions.device.write,
        targetPerms["device"]!!.second,
        editor.permissions.device.write.state
    )
    checkAndAdd(
        "Device",
        "Delete",
        ctx.myPermissions.device.delete,
        targetPerms["device"]!!.third,
        editor.permissions.device.delete.state
    )

    checkAndAdd(
        "Location",
        "View",
        ctx.myPermissions.location.read,
        targetPerms["location"]!!.first,
        editor.permissions.location.read.state
    )
    checkAndAdd(
        "Location",
        "Edit",
        ctx.myPermissions.location.write,
        targetPerms["location"]!!.second,
        editor.permissions.location.write.state
    )
    checkAndAdd(
        "Location",
        "Delete",
        ctx.myPermissions.location.delete,
        targetPerms["location"]!!.third,
        editor.permissions.location.delete.state
    )

    checkAndAdd(
        "User",
        "View",
        ctx.myPermissions.user.read,
        targetPerms["user"]!!.first,
        editor.permissions.user.read.state
    )
    checkAndAdd(
        "User",
        "Edit",
        ctx.myPermissions.user.write,
        targetPerms["user"]!!.second,
        editor.permissions.user.write.state
    )
    checkAndAdd(
        "User",
        "Delete",
        ctx.myPermissions.user.delete,
        targetPerms["user"]!!.third,
        editor.permissions.user.delete.state
    )

    checkAndAdd(
        "Company",
        "View",
        ctx.myPermissions.company.read,
        targetPerms["company"]!!.first,
        editor.permissions.company.read.state
    )
    checkAndAdd(
        "Company",
        "Edit",
        ctx.myPermissions.company.write,
        targetPerms["company"]!!.second,
        editor.permissions.company.write.state
    )
    checkAndAdd(
        "Company",
        "Delete",
        ctx.myPermissions.company.delete,
        targetPerms["company"]!!.third,
        editor.permissions.company.delete.state
    )

    if (missingPermissions.isNotEmpty()) {
        onWarning(missingPermissions)
    } else {
        editor.permissions.media.read.set(targetPerms["media"]!!.first)
        editor.permissions.media.write.set(targetPerms["media"]!!.second)
        editor.permissions.media.delete.set(targetPerms["media"]!!.third)

        editor.permissions.campaign.read.set(targetPerms["campaign"]!!.first)
        editor.permissions.campaign.write.set(targetPerms["campaign"]!!.second)
        editor.permissions.campaign.delete.set(targetPerms["campaign"]!!.third)

        editor.permissions.device.read.set(targetPerms["device"]!!.first)
        editor.permissions.device.write.set(targetPerms["device"]!!.second)
        editor.permissions.device.delete.set(targetPerms["device"]!!.third)

        editor.permissions.location.read.set(targetPerms["location"]!!.first)
        editor.permissions.location.write.set(targetPerms["location"]!!.second)
        editor.permissions.location.delete.set(targetPerms["location"]!!.third)

        editor.permissions.user.read.set(targetPerms["user"]!!.first)
        editor.permissions.user.write.set(targetPerms["user"]!!.second)
        editor.permissions.user.delete.set(targetPerms["user"]!!.third)

        editor.permissions.company.read.set(targetPerms["company"]!!.first)
        editor.permissions.company.write.set(targetPerms["company"]!!.second)
        editor.permissions.company.delete.set(targetPerms["company"]!!.third)
    }
}

@Composable
context(ctx: UserECtx)
private fun RolesSection(editor: UserECtx.Editor) {
    var showWarningDialog by remember { mutableStateOf<List<String>?>(null) }

    if (showWarningDialog != null) {
        AlertDialog(
            onDismissRequest = { showWarningDialog = null },
            title = { Text(stringResource(Res.string.ui_users_edit_role_warning_title)) },
            text = {
                Text(
                    stringResource(
                        Res.string.ui_users_edit_role_warning_message,
                        showWarningDialog!!.joinToString("\n") { "• $it" }
                    )
                )
            },
            confirmButton = {
                TextButton(onClick = { showWarningDialog = null }) {
                    Text(stringResource(Res.string.ui_users_edit_btn_ok))
                }
            }
        )
    }

    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            LabelSection(text = stringResource(Res.string.ui_users_edit_section_roles))

            Spacer(modifier = Modifier.height(16.dp))

            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                RoleItem(
                    title = stringResource(Res.string.ui_users_edit_role_admin),
                    onClick = { applyRole(UserRole.Admin, editor) { showWarningDialog = it } }
                )
                RoleItem(
                    title = stringResource(Res.string.ui_users_edit_role_technic),
                    onClick = { applyRole(UserRole.Technic, editor) { showWarningDialog = it } }
                )
                RoleItem(
                    title = stringResource(Res.string.ui_users_edit_role_campaign_publisher),
                    onClick = {
                        applyRole(UserRole.CampaignPublisher, editor) {
                            showWarningDialog = it
                        }
                    }
                )
                RoleItem(
                    title = stringResource(Res.string.ui_users_edit_role_campaign_viewer),
                    onClick = {
                        applyRole(UserRole.CampaignViewer, editor) {
                            showWarningDialog = it
                        }
                    }
                )
            }
        }
    }
}

@Composable
private fun RoleItem(title: String, onClick: () -> Unit) {
    OutlinedButton(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(4.dp),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        colors = ButtonDefaults.outlinedButtonColors(
            contentColor = MaterialTheme.colorScheme.primary
        )
    ) {
        Text(
            text = title.uppercase(),
            fontWeight = FontWeight.Bold,
            style = MaterialTheme.typography.labelLarge
        )
    }
}

